/**
 * English: Primary navigation sidebar — supports collapsed (icon-only) and expanded modes.
 * Thai: sidebar นำทางหลัก — รองรับโหมดยุบ (icon-only) และขยาย
 */
import { NavLink, useLocation } from 'react-router-dom';
import type { ComponentType, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { languageButtonLabel } from '../../utils/i18nLocale';
import {
  HomeIcon,
  CpuChipIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  HeartIcon,
  ChartBarIcon,
  BookOpenIcon,
  UserGroupIcon,
  InboxIcon,
  WrenchScrewdriverIcon,
  LanguageIcon,
  MapPinIcon,
  ShieldCheckIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import { useLayout } from '../../context/LayoutContext';

type NavItem = { to: string; icon: ComponentType<SVGProps<SVGSVGElement>>; label: string };

const getNavGroups = (t: (key: string) => string): { title: string; items: NavItem[] }[] => [
  {
    title: t('sidebar.group.home'),
    items: [
      { to: '/', icon: HomeIcon, label: t('sidebar.menu.dashboard') },
      { to: '/organizations', icon: BuildingOffice2Icon, label: t('sidebar.menu.organizations') },
    ],
  },
  {
    title: t('sidebar.group.chat'),
    items: [
      { to: '/chat-test', icon: ChatBubbleLeftRightIcon, label: t('sidebar.menu.agentChat') },
    ],
  },
  {
    title: t('sidebar.group.device'),
    items: [{ to: '/tasks', icon: InboxIcon, label: t('sidebar.menu.taskHistory') }],
  },
  {
    title: t('sidebar.group.ai'),
    items: [
      { to: '/agent-management', icon: UserGroupIcon, label: t('sidebar.menu.agents') },
      { to: '/tools', icon: WrenchScrewdriverIcon, label: t('sidebar.menu.agentTools') },
      { to: '/guardrails', icon: ShieldCheckIcon, label: t('guardrails.pageTitle') },
      { to: '/usage', icon: ChartBarIcon, label: t('sidebar.menu.llmUsage') },
    ],
  },
  {
    title: t('sidebar.group.system'),
    items: [
      { to: '/status', icon: HeartIcon, label: t('sidebar.menu.systemStatus') },
      { to: '/docs', icon: BookOpenIcon, label: t('sidebar.menu.docs') },
      { to: '/settings', icon: Cog6ToothIcon, label: t('sidebar.menu.settings') },
    ],
  },
];

interface SidebarProps {
  /** Force expanded (ignores collapse state) — used in mobile drawer */
  forceExpanded?: boolean;
  /** Desktop hover-rail: labels show when parent expands on hover/pin */
  hoverRail?: boolean;
  /** Parent-driven expanded state (hover rail) */
  expanded?: boolean;
  /** Called when a nav link is clicked — used to close mobile drawer */
  onNavigate?: () => void;
  /** Desktop rail: toggle expand/collapse from footer or edge tab */
  onRailToggle?: () => void;
  isPinned?: boolean;
}

const RAIL_LABEL_TRANSITION =
  'transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none';

function railFadeClass(show: boolean): string {
  return [
    'whitespace-nowrap overflow-hidden inline-block',
    RAIL_LABEL_TRANSITION,
    show ? 'max-w-[11rem] opacity-100 translate-x-0' : 'max-w-0 opacity-0 -translate-x-1',
  ].join(' ');
}

function railRevealClass(show: boolean): string {
  return [
    'whitespace-nowrap overflow-hidden inline-block',
    RAIL_LABEL_TRANSITION,
    show ? 'max-w-[11rem] opacity-100' : 'max-w-0 opacity-0',
  ].join(' ');
}

export default function Sidebar({
  forceExpanded,
  hoverRail,
  expanded: expandedProp,
  onNavigate,
  onRailToggle,
  isPinned: isPinnedProp,
}: SidebarProps) {
  const {
    isSidebarCollapsed,
    toggleSidebarCollapse,
    isSidebarPinned: isPinnedCtx,
  } = useLayout();
  const isSidebarPinned = isPinnedProp ?? isPinnedCtx;
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const expanded =
    forceExpanded ||
    (hoverRail ? Boolean(expandedProp) : !isSidebarCollapsed);
  const collapsed = !expanded;
  const showLabels = hoverRail ? expanded : !collapsed;

  const navGroups = getNavGroups(t);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'th' ? 'en' : 'th');
  };

  return (
    <aside
      className={`app-glass-panel border-r border-app-border/80 flex flex-col h-full w-full min-w-0 backdrop-blur-xl ${
        hoverRail ? '' : `shrink-0 transition-all duration-200 ${collapsed ? 'w-16' : 'w-56'}`
      }`}
    >
      {/* Logo */}
      <div
        className={`h-14 flex items-center border-b border-app-border/70 shrink-0 transition-[padding,gap] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
          showLabels ? 'px-4 gap-2.5' : 'justify-center px-0'
        }`}
      >
        <span className="grid h-8 w-8 place-items-center rounded-full bg-app-accent/10 ring-1 ring-app-accent/20 shrink-0">
          <CpuChipIcon className="w-5 h-5 text-app-accent" />
        </span>
        <span
          className={`text-base font-semibold text-app-text tracking-tight ${
            hoverRail ? railFadeClass(showLabels) : collapsed ? 'hidden' : ''
          }`}
        >
          AgentStation
        </span>
      </div>

      {/* Navigation */}
      <nav
        className={`flex-1 py-3 overflow-y-auto overflow-x-hidden space-y-4 transition-[padding] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
          showLabels ? 'px-2' : 'px-1.5'
        }`}
      >
        {navGroups.map((group) => (
          <div key={group.title} className={showLabels ? '' : 'space-y-0'}>
            <p
              className={`px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-app-text-dim overflow-hidden ${RAIL_LABEL_TRANSITION} ${
                hoverRail
                  ? showLabels
                    ? 'max-h-6 opacity-100'
                    : 'max-h-0 opacity-0 mb-0'
                  : showLabels
                    ? ''
                    : 'hidden'
              }`}
            >
              {group.title}
            </p>
            <div className="space-y-0.5">
              {group.items.map(({ to, icon: Icon, label }) => {
                const isActive = to === '/'
                  ? location.pathname === '/'
                  : to === '/tasks'
                    ? location.pathname === '/tasks' || location.pathname.startsWith('/tasks/')
                    : location.pathname.startsWith(to);

                return (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={onNavigate}
                    className={`flex items-center rounded-lg text-sm font-medium transition-[colors,padding] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
                      showLabels ? 'px-3 py-2' : 'justify-center px-2 py-2.5'
                    } ${
                      isActive
                        ? 'bg-app-accent text-white shadow-soft'
                        : 'text-app-text-sec hover:bg-app-muted/70 hover:text-app-text'
                    }`}
                    title={!showLabels ? label : undefined}
                  >
                    <Icon
                      className={`w-4 h-4 shrink-0 transition-[margin] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
                        showLabels ? 'mr-2.5' : 'mr-0'
                      }`}
                    />
                    <span className={hoverRail ? railRevealClass(showLabels) : collapsed ? 'hidden' : ''}>
                      {label}
                    </span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer with language and collapse toggle */}
      <div
        className={`border-t border-app-border/70 flex flex-col gap-2 transition-[padding] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
          showLabels ? 'p-3' : 'py-3 items-center'
        }`}
      >
        <div
          className={`flex w-full ${
            showLabels ? 'flex-row items-center justify-between' : 'flex-col items-center gap-2'
          }`}
        >
          <button
            type="button"
            onClick={toggleLanguage}
            className={`flex items-center rounded-md text-app-text-sec hover:bg-app-muted hover:text-app-text transition-colors duration-200 ${
              showLabels ? 'gap-2 p-1.5' : 'p-2 justify-center'
            }`}
            title={t('sidebar.toggleLanguage')}
          >
            <LanguageIcon className="w-4 h-4 shrink-0" />
            <span className={`text-xs font-medium uppercase ${hoverRail ? railFadeClass(showLabels) : collapsed ? 'hidden' : ''}`}>
              {languageButtonLabel(i18n.language)}
            </span>
          </button>

          {!forceExpanded && hoverRail && onRailToggle && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRailToggle();
              }}
              onPointerDown={(e) => e.stopPropagation()}
              className={`rounded-md transition-colors ${
                showLabels ? 'p-1.5' : 'p-2'
              } ${
                isSidebarPinned
                  ? 'text-app-accent bg-app-accent/10'
                  : 'text-app-text-dim hover:bg-app-muted hover:text-app-text'
              }`}
              title={isSidebarPinned ? t('sidebar.unpinSidebar') : t('sidebar.pinSidebar')}
              aria-label={isSidebarPinned ? t('sidebar.unpinSidebar') : t('sidebar.pinSidebar')}
            >
              <MapPinIcon className="w-4 h-4 shrink-0" />
            </button>
          )}
          {!forceExpanded && !hoverRail && (
            <button
              type="button"
              onClick={toggleSidebarCollapse}
              className="p-1.5 rounded-md text-app-text-dim hover:bg-app-muted hover:text-app-text transition-colors"
              title={collapsed ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar')}
            >
              <span className="text-[10px] font-medium">{collapsed ? '»' : '«'}</span>
            </button>
          )}
        </div>
        
        <div
          className={`px-1.5 text-[10px] text-app-text-dim text-center w-full ${
            hoverRail ? (showLabels ? railFadeClass(true) : '') : collapsed ? 'hidden' : ''
          }`}
        >
          {hoverRail && !showLabels ? (
            <span className="block leading-tight" title={t('sidebar.hoverToOpen')}>
              ⋮
            </span>
          ) : (
            t('sidebar.adminVersion')
          )}
        </div>
      </div>
    </aside>
  );
}
